// ── 매출 입력/수정 모달 (입금·세금계산서·카드전화승인·기타) ─────────────────────
import React, { useState } from 'react';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../../hooks/useOverlayClickClose';
import {
  overlayStyle, modalBox, modalTitle, modalIconBox, modalTitleText, modalCloseBtn,
  fieldRow, labelTxt,
  fullInput, fullSelect, modalBtns, btnCancel, btnSave,
} from './styles';

const INSTALLMENT_PRESETS = [
  { label: '일시불', value: '0' },
  { label: '2개월', value: '2' },
  { label: '3개월', value: '3' },
  { label: '6개월', value: '6' },
  { label: '12개월', value: '12' },
];

function cleanInstallmentInput(value) {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.replace(/^0+(?=\d)/, '');
}

function validateInstallmentInput(value) {
  const cleaned = cleanInstallmentInput(value);
  const months = cleaned === '' ? 0 : Number(cleaned);
  if (!Number.isInteger(months) || months < 0 || months > 99) {
    return { ok: false, message: '할부개월은 0개월 또는 2~99개월로 입력해 주세요.' };
  }
  if (months === 1) {
    return { ok: false, message: '일시불은 0개월로, 할부는 2개월 이상으로 입력해 주세요.' };
  }
  return { ok: true, value: months };
}

/** 입금 입력/수정 모달 */
export function PaymentModal({ initial, taxInvoices, salesUsers, onSave, onClose }) {
  useBodyScrollLock(true);
  const overlayCloseProps = useOverlayClickClose(onClose);
  const [form, setForm] = useState({
    payment_date:   initial?.payment_date   || '',
    payer_name:     initial?.payer_name     || '',
    amount:         initial?.amount != null ? String(initial.amount) : '',
    user_id:        initial?.user_id        || '',
    tax_invoice_id: initial?.tax_invoice_id || '',
    note:           initial?.note           || '',
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  return (
    <div style={overlayStyle} {...overlayCloseProps}>
      <div style={modalBox} onClick={(e) => e.stopPropagation()}>
        <div style={modalTitle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={modalIconBox}>🏦</div>
            <span style={modalTitleText}>{initial?.id ? '입금 수정' : '입금 등록'}</span>
          </div>
          <button style={modalCloseBtn} onClick={onClose}>✕</button>
        </div>
        <div style={fieldRow}><label style={labelTxt}>입금일자 *</label><input type="date" style={fullInput} value={form.payment_date} onChange={(e) => set('payment_date', e.target.value)} /></div>
        <div style={fieldRow}><label style={labelTxt}>입금자 *</label><input style={fullInput} value={form.payer_name} onChange={(e) => set('payer_name', e.target.value)} placeholder="입금자(상호)" /></div>
        <div style={fieldRow}><label style={labelTxt}>금액 *</label><input style={fullInput} value={form.amount} onChange={(e) => set('amount', e.target.value)} placeholder="숫자만 입력" /></div>
        <div style={fieldRow}><label style={labelTxt}>담당 마케터</label>
          <select style={fullSelect} value={form.user_id} onChange={(e) => set('user_id', e.target.value)}>
            <option value="">선택 안함</option>
            {salesUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div style={fieldRow}><label style={labelTxt}>세금계산서 연결</label>
          <select style={fullSelect} value={form.tax_invoice_id} onChange={(e) => set('tax_invoice_id', e.target.value)}>
            <option value="">연결 안함</option>
            {/* 미처리 잔액이 있는 것 표시. 양수=미입금 남음, 음수=환불 입금 미연결.
                현재 연결된 항목은 완납이어도 포함. */}
            {taxInvoices
              .filter((ti) => Number(ti.unpaid_amount) !== 0 || String(ti.id) === String(form.tax_invoice_id))
              .map((ti) => {
                const unpaid = Number(ti.unpaid_amount);
                return (
                  <option key={ti.id} value={ti.id}>
                    [{ti.issue_date}] {ti.customer_name || '-'} {Number(ti.total_amount).toLocaleString()}원
                    {unpaid > 0
                      ? ` (${unpaid.toLocaleString()}원 남음)`
                      : unpaid < 0
                        ? ` (환불 ${Math.abs(unpaid).toLocaleString()}원 연결 필요)`
                        : ' ✅ 완납'}
                  </option>
                );
              })
            }
          </select>
        </div>
        <div style={fieldRow}><label style={labelTxt}>비고</label><input style={fullInput} value={form.note} onChange={(e) => set('note', e.target.value)} /></div>
        <div style={modalBtns}>
          <button style={btnCancel} onClick={onClose}>취소</button>
          <button style={btnSave} onClick={() => onSave(form)}>저장</button>
        </div>
      </div>
    </div>
  );
}

/** 세금계산서 입력/수정 모달 */
export function TaxInvoiceModal({ initial, salesUsers, onSave, onClose }) {
  useBodyScrollLock(true);
  const overlayCloseProps = useOverlayClickClose(onClose);
  const [form, setForm] = useState({
    issue_type:    initial?.issue_type    || '영수',
    issue_date:    initial?.issue_date    || '',
    supply_amount: initial?.supply_amount != null ? String(initial.supply_amount) : '',
    tax_amount:    initial?.tax_amount    != null ? String(initial.tax_amount)    : '',
    total_amount:  initial?.total_amount  != null ? String(initial.total_amount)  : '',
    customer_name: initial?.customer_name || '',
    biz_no:        initial?.biz_no        || '',
    note:          initial?.note          || '',
    user_id:       initial?.user_id       || '',
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // 공급가액·세액 입력 시 발행금액 자동 계산
  function handleSupplyChange(v) {
    const supply = Number(String(v).replace(/,/g, ''));
    const tax    = Number(String(form.tax_amount).replace(/,/g, ''));
    set('supply_amount', v);
    if (Number.isFinite(supply) && Number.isFinite(tax)) set('total_amount', String(supply + tax));
  }
  function handleTaxChange(v) {
    const supply = Number(String(form.supply_amount).replace(/,/g, ''));
    const tax    = Number(String(v).replace(/,/g, ''));
    set('tax_amount', v);
    if (Number.isFinite(supply) && Number.isFinite(tax)) set('total_amount', String(supply + tax));
  }

  return (
    <div style={overlayStyle} {...overlayCloseProps}>
      <div style={modalBox} onClick={(e) => e.stopPropagation()}>
        <div style={modalTitle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={modalIconBox}>🧾</div>
            <span style={modalTitleText}>{initial?.id ? '세금계산서 수정' : '세금계산서 등록'}</span>
          </div>
          <button style={modalCloseBtn} onClick={onClose}>✕</button>
        </div>
        <div style={fieldRow}><label style={labelTxt}>발행일자 *</label><input type="date" style={fullInput} value={form.issue_date} onChange={(e) => set('issue_date', e.target.value)} /></div>
        <div style={fieldRow}><label style={labelTxt}>구분</label>
          <select style={fullSelect} value={form.issue_type} onChange={(e) => set('issue_type', e.target.value)}>
            <option value="영수">영수</option><option value="청구">청구</option>
          </select>
        </div>
        <div style={fieldRow}><label style={labelTxt}>상호</label><input style={fullInput} value={form.customer_name} onChange={(e) => set('customer_name', e.target.value)} /></div>
        <div style={fieldRow}><label style={labelTxt}>사업자번호</label><input style={fullInput} value={form.biz_no} onChange={(e) => set('biz_no', e.target.value)} placeholder="000-00-00000" /></div>
        <div style={fieldRow}><label style={labelTxt}>공급가액</label><input style={fullInput} value={form.supply_amount} onChange={(e) => handleSupplyChange(e.target.value)} placeholder="부가세 제외 금액" /></div>
        <div style={fieldRow}><label style={labelTxt}>세액</label><input style={fullInput} value={form.tax_amount} onChange={(e) => handleTaxChange(e.target.value)} placeholder="부가세" /></div>
        <div style={fieldRow}><label style={labelTxt}>발행금액 (합계)</label><input style={fullInput} value={form.total_amount} onChange={(e) => set('total_amount', e.target.value)} /></div>
        <div style={fieldRow}><label style={labelTxt}>마케터</label>
          <select style={fullSelect} value={form.user_id} onChange={(e) => set('user_id', e.target.value)}>
            <option value="">선택 안함</option>
            {(salesUsers || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div style={fieldRow}><label style={labelTxt}>비고</label><input style={fullInput} value={form.note} onChange={(e) => set('note', e.target.value)} /></div>
        <div style={modalBtns}>
          <button style={btnCancel} onClick={onClose}>취소</button>
          <button style={btnSave} onClick={() => onSave(form)}>저장</button>
        </div>
      </div>
    </div>
  );
}

/** 카드전화승인 입력/수정 모달 */
export function CardPhoneModal({ initial, salesUsers, onSave, onClose }) {
  useBodyScrollLock(true);
  const overlayCloseProps = useOverlayClickClose(onClose);
  const [form, setForm] = useState({
    payment_date:       initial?.payment_date || '',
    card_company:       initial?.card_company || '',
    amount:             initial?.amount != null ? String(initial.amount) : '',
    installment_months: initial?.installment_months != null ? String(initial.installment_months) : '0',
    user_id:            initial?.user_id || '',
    note:               initial?.note    || '',
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setInstallmentMonths = (value) => set('installment_months', cleanInstallmentInput(value));
  const handleSave = () => {
    const result = validateInstallmentInput(form.installment_months);
    if (!result.ok) {
      window.alert(result.message);
      return;
    }
    onSave({ ...form, installment_months: String(result.value) });
  };
  return (
    <div style={overlayStyle} {...overlayCloseProps}>
      <div style={modalBox} onClick={(e) => e.stopPropagation()}>
        <div style={modalTitle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={modalIconBox}>💳</div>
            <span style={modalTitleText}>{initial?.id ? '카드전화승인 수정' : '카드전화승인 등록'}</span>
          </div>
          <button style={modalCloseBtn} onClick={onClose}>✕</button>
        </div>
        <div style={fieldRow}><label style={labelTxt}>결제일 *</label><input type="date" style={fullInput} value={form.payment_date} onChange={(e) => set('payment_date', e.target.value)} /></div>
        <div style={fieldRow}><label style={labelTxt}>카드사 *</label><input style={fullInput} value={form.card_company} onChange={(e) => set('card_company', e.target.value)} placeholder="국민·신한·하나 등" /></div>
        <div style={fieldRow}><label style={labelTxt}>결제금액 *</label><input style={fullInput} value={form.amount} onChange={(e) => set('amount', e.target.value)} /></div>
        <div style={fieldRow}><label style={labelTxt}>할부</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 92px auto', gap: 6, alignItems: 'center' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {INSTALLMENT_PRESETS.map((item) => {
                const active = String(form.installment_months || '0') === item.value;
                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => setInstallmentMonths(item.value)}
                    style={{
                      // 활성 = 표준 알약 토글 톤(연파랑 배경 + 파랑 글씨) — 파랑 primary 제거
                      border: '1px solid var(--border)',
                      background: active ? 'var(--std-pill-on-bg, rgba(59,154,242,0.12))' : 'var(--bg-card)',
                      color: active ? 'var(--std-pill-on-text, #1d7de6)' : 'var(--text)',
                      borderRadius: 8,
                      padding: '7px 9px',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
            <input
              style={{ ...fullInput, textAlign: 'right' }}
              value={form.installment_months}
              onChange={(e) => setInstallmentMonths(e.target.value)}
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="13"
            />
            <span style={{ fontSize: 13, color: 'var(--text-sub)' }}>개월</span>
          </div>
        </div>
        <div style={fieldRow}><label style={labelTxt}>마케터</label>
          <select style={fullSelect} value={form.user_id} onChange={(e) => set('user_id', e.target.value)}>
            <option value="">선택 안함</option>
            {salesUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div style={fieldRow}><label style={labelTxt}>비고</label><input style={fullInput} value={form.note} onChange={(e) => set('note', e.target.value)} /></div>
        <div style={modalBtns}>
          <button style={btnCancel} onClick={onClose}>취소</button>
          <button style={btnSave} onClick={handleSave}>저장</button>
        </div>
      </div>
    </div>
  );
}

/** 기타 매출 입력/수정 모달 */
export function OtherModal({ initial, salesUsers, onSave, onClose }) {
  useBodyScrollLock(true);
  const overlayCloseProps = useOverlayClickClose(onClose);
  const [form, setForm] = useState({
    date:        initial?.date        || '',
    amount:      initial?.amount != null ? String(initial.amount) : '',
    description: initial?.description || '',
    user_id:     initial?.user_id     || '',
    note:        initial?.note        || '',
  });
  const [userErr, setUserErr] = useState(false); // 마케터 미선택 오류 표시 플래그
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // 저장 전 마케터 선택 여부 검증 (user_id 없으면 정산서에 반영 불가)
  function handleSave() {
    if (!form.user_id) { setUserErr(true); return; }
    setUserErr(false);
    onSave(form);
  }

  return (
    <div style={overlayStyle} {...overlayCloseProps}>
      <div style={modalBox} onClick={(e) => e.stopPropagation()}>
        <div style={modalTitle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={modalIconBox}>💰</div>
            <span style={modalTitleText}>{initial?.id ? '기타 수정' : '기타 매출 등록'}</span>
          </div>
          <button style={modalCloseBtn} onClick={onClose}>✕</button>
        </div>
        <div style={fieldRow}><label style={labelTxt}>날짜 *</label><input type="date" style={fullInput} value={form.date} onChange={(e) => set('date', e.target.value)} /></div>
        <div style={fieldRow}><label style={labelTxt}>금액 *</label><input style={fullInput} value={form.amount} onChange={(e) => set('amount', e.target.value)} /></div>
        <div style={fieldRow}><label style={labelTxt}>내용 *</label><input style={fullInput} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="매출 내용" /></div>
        {/* 마케터 필수 선택 — 미지정 시 정산서에 반영되지 않으므로 반드시 선택 */}
        <div style={fieldRow}>
          <label style={labelTxt}>마케터 *</label>
          <select
            style={{ ...fullSelect, borderColor: userErr ? '#dc2626' : undefined }}
            value={form.user_id}
            onChange={(e) => { set('user_id', e.target.value); setUserErr(false); }}
          >
            <option value="">마케터를 선택하세요</option>
            {salesUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          {userErr && (
            <span style={{ fontSize: 11, color: '#dc2626', marginTop: 3, display: 'block' }}>
              마케터를 선택해야 정산서에 반영됩니다.
            </span>
          )}
        </div>
        <div style={fieldRow}><label style={labelTxt}>비고</label><input style={fullInput} value={form.note} onChange={(e) => set('note', e.target.value)} /></div>
        <div style={modalBtns}>
          <button style={btnCancel} onClick={onClose}>취소</button>
          <button style={btnSave} onClick={handleSave}>저장</button>
        </div>
      </div>
    </div>
  );
}
